import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { perfEnabled, perfMark, perfMeasure } from '@core/perf/perf-marks';

/**
 * AT-9296 — mierzy KAŻDE wywołanie API z punktu widzenia aplikacji.
 *
 * Resource Timing mówi, ile trwało żądanie w sieci. To jest co innego: tu widać moment,
 * w którym aplikacja ZDECYDOWAŁA się je wysłać, oraz jak długo czekała na odpowiedź, zanim
 * ruszyła dalej. Różnica między tymi dwoma widokami to czas spędzony w kolejce przeglądarki
 * i w warstwie Angulara — dokładnie ten, którego nie widać w zakładce Sieć.
 *
 * Po co: kaskada startowa strony klienta ma cztery SEKWENCYJNE fale. Żeby ją skrócić, trzeba
 * wiedzieć, co czeka na co — a to jest decyzja frontu, nie właściwość sieci.
 *
 * Interceptor jest bezkosztowy przy zamkniętej bramce (patrz `perf-marks.ts`).
 */
@Injectable()
export class PerfTimingInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!perfEnabled()) {
      return next.handle(request);
    }

    // Nazwa odcinka to sama ścieżka: adresy niosą parametry (`page_id`, `currency`), przez
    // które ten sam endpoint miałby dziesiątki różnych nazw i nie dałoby się ich zsumować.
    const sciezka = this.sciezka(request.url);
    const nazwa = `api ${request.method} ${sciezka}`;
    const start = `${nazwa}-start`;

    perfMark(start);

    return next.handle(request).pipe(
      tap({
        next: (zdarzenie) => {
          // `next` dostaje też zdarzenia postępu; odcinek zamykamy dopiero na odpowiedzi.
          if (zdarzenie instanceof HttpResponse) {
            perfMeasure(nazwa, start);
          }
        },
        // Błąd też domyka odcinek — wywołanie, które padło po 3 sekundach, jest tak samo
        // częścią kaskady jak udane. Bez tego znikałoby z pomiaru akurat wtedy, gdy boli.
        error: () => perfMeasure(`${nazwa} [blad]`, start),
      })
    );
  }

  /** Ścieżka bez originu i bez parametrów zapytania. */
  private sciezka(url: string): string {
    const bezParametrow = url.split('?')[0];

    try {
      return new URL(bezParametrow, 'http://x').pathname;
    } catch (e) {
      return bezParametrow;
    }
  }
}
